import { Box, IconButton, InputAdornment, Typography, alpha } from '@mui/material';
import Slide from '@mui/material/Slide/Slide';
import TextField from '@mui/material/TextField/TextField';
import { Close, Search } from '@mui/icons-material';
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchBar } from '../Stores/SearchBarProvider';
import TooltipHardened from './TooltipHardened';
import useDataTable from '../Providers/DataTableProvider';

interface Props {
  sticky?: boolean;
}

export const NavigationSearchBar = ({ sticky }: Props): React.JSX.Element => {
  const { committedFilter, triggerSearch, clearSearch, hasColumnFilterInput, clearColumnFilters } = useDataTable();
  const { searchText, setSearchText, isInputDisabled, isHidden } = useSearchBar();

  const [isOpen, setOpen] = useState<boolean>(false);
  const isSticky = sticky == true;

  const committedSearch = committedFilter.search ?? '';
  const hasUncommittedChanges = searchText !== committedSearch;
  const hasActiveSearch = committedSearch !== '';

  // Search icon: text differs from committed (new search to submit)
  // Clear icon: text matches committed and is non-empty (clear active search)
  const showClear = !hasUncommittedChanges && hasActiveSearch;

  /** on sticky */
  useEffect(() => {
    setOpen(isSticky && searchText !== '');
  }, [isSticky]);

  const buttonAdornment = useMemo(
    () => (
      <InputAdornment position={'end'}>
        <TooltipHardened tooltip={showClear ? 'Clear' : 'Search'} placement={'bottom'}>
          <IconButton
            sx={{ color: 'header.contrastText' }}
            disabled={hasColumnFilterInput}
            onClick={() => (showClear ? clearSearch?.() : triggerSearch?.())}
          >
            {showClear ? <Close /> : <Search />}
          </IconButton>
        </TooltipHardened>
      </InputAdornment>
    ),
    [showClear, hasColumnFilterInput, triggerSearch, clearSearch],
  );

  const inputElement = useMemo(() => {
    const isStickyOpen = isSticky && isOpen;
    const isStickyClosed = isSticky && !isOpen;

    return (
      <TextField
        value={searchText}
        variant={'filled'}
        type={'text'}
        label={'Search'}
        disabled={isInputDisabled || hasColumnFilterInput}
        onChange={(e) => setSearchText(e.target.value)}
        onKeyDown={(e) => {
          const { key } = e;
          switch (key) {
            case 'Enter':
              triggerSearch?.();
              break;
            case 'Escape':
              clearSearch?.();
              break;
          }
        }}
        sx={(theme) => ({
          minWidth: 200,
          width: '100%',
          borderRadius: 48,
          backgroundColor: alpha(theme.palette.header.contrastText, 0.12),
          ...(isStickyOpen && {
            boxShadow: theme.shadows[5],
            ':hover': {
              backgroundColor: alpha(theme.palette.header.contrastText, 0.2),
            },
          }),
          ...(isStickyClosed && {
            backgroundColor: 'transparent',
          }),
        })}
        InputProps={{
          sx: {
            p: 0,
            backgroundColor: 'transparent',
            color: 'header.contrastText',
            '&:hover': {
              backgroundColor: 'transparent',
            },
            '&.Mui-focused': {
              backgroundColor: 'transparent',
            },
            '&.Mui-disabled': {
              backgroundColor: 'transparent',
            },
          },
          disableUnderline: true,
          endAdornment: buttonAdornment,
        }}
      />
    );
  }, [isSticky, buttonAdornment, searchText, isInputDisabled, hasColumnFilterInput, isOpen]);

  const hintElement = useMemo(() => {
    if (!hasColumnFilterInput) {
      return null;
    }

    return (
      <Typography
        variant="caption"
        onClick={() => clearColumnFilters?.()}
        // Absolutely positioned so the hint text doesn't push the
        // search bar wider than its natural width.
        sx={(theme) => ({
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          color: alpha(theme.palette.header.contrastText, 0.7),
          cursor: 'pointer',
          textAlign: 'center',
          mt: 0.5,
          '&:hover': {
            color: theme.palette.header.contrastText,
            textDecoration: 'underline',
          },
        })}
      >
        Column filter active — click to clear
      </Typography>
    );
  }, [hasColumnFilterInput, clearColumnFilters]);

  return (
    <Slide in={!isHidden} direction={'down'} mountOnEnter unmountOnExit>
      {/* Wrapper needed because Slide requires a single ref-forwarding child.
          Opacity dims the entire search bar when column filters are active. */}
      <Box
        sx={{
          position: 'relative',
          ...(hasColumnFilterInput && { opacity: 0.5 }),
        }}
      >
        {inputElement}
        {hintElement}
      </Box>
    </Slide>
  );
};
